/**
 * InventarTab - Gestiune produse și materiale
 * Cu import AI din facturi (PDF, Excel, XML)
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Package, Plus, Trash2, Edit, Search, Upload, FileText, 
  TrendingDown, TrendingUp, History, AlertTriangle, Check,
  Loader2, Sparkles, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, Square
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventarTab({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("products");
  
  // Selection state for bulk delete
  const [selectedItems, setSelectedItems] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Sorting state
  const [sortKey, setSortKey] = useState(null); // 'name', 'created_at', 'material_price', 'supplier'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // New product form
  const [newItem, setNewItem] = useState({
    name: "",
    um: "buc",
    material_price: 0,
    manopera_price: 0,
    stock: 0,
    supplier: "",
    category: "",
    code: "",
    invoice_series: "",
    invoice_number: "",
    invoice_date: ""
  });
  
  // AI Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importedItems, setImportedItems] = useState([]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory`, { params: { company_id: companyId } });
      setItems(res.data.items || []);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast.error("Eroare la încărcarea inventarului");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/inventory/logs`, { params: { company_id: companyId, limit: 50 } });
      setLogs(res.data.logs || []);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchLogs();
  }, [companyId]);

  const handleAddItem = async () => {
    if (!newItem.name.trim()) {
      toast.error("Numele produsului este obligatoriu");
      return;
    }
    
    try {
      await axios.post(`${API}/inventory`, newItem, { params: { company_id: companyId } });
      toast.success("Produs adăugat cu succes");
      setShowAddDialog(false);
      setNewItem({
        name: "",
        um: "buc",
        material_price: 0,
        manopera_price: 0,
        stock: 0,
        supplier: "",
        category: "",
        code: "",
        invoice_series: "",
        invoice_number: "",
        invoice_date: ""
      });
      fetchItems();
    } catch (error) {
      toast.error("Eroare la adăugarea produsului");
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    
    try {
      await axios.put(`${API}/inventory/${editingItem.id}`, editingItem, { params: { company_id: companyId } });
      toast.success("Produs actualizat");
      setEditingItem(null);
      fetchItems();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm("Sigur doriți să ștergeți acest produs?")) return;
    
    try {
      await axios.delete(`${API}/inventory/${itemId}`, { params: { company_id: companyId } });
      toast.success("Produs șters");
      fetchItems();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Bulk delete - ștergere în grup
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error("Selectați cel puțin un produs");
      return;
    }
    
    if (!confirm(`Sigur doriți să ștergeți ${selectedItems.length} produse selectate?`)) return;
    
    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${API}/inventory/bulk-delete?company_id=${companyId}`,
        selectedItems
      );
      toast.success(`${response.data.deleted_count} produse șterse!`);
      setSelectedItems([]);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergere în grup");
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle select item
  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Select all / Deselect all
  const toggleSelectAll = () => {
    if (selectedItems.length === sortedItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(sortedItems.map(item => item.id));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setImportFile(file);
    } else {
      toast.error("Vă rugăm selectați un fișier PDF");
    }
  };

  const handleAIImport = async () => {
    if (!importFile) {
      toast.error("Selectați un fișier");
      return;
    }
    
    setImporting(true);
    setImportedItems([]);
    
    try {
      // Read file and convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        try {
          // Call AI to extract products from document
          const response = await axios.post(`${API}/ai/extract-products`, {
            file_base64: base64,
            filename: importFile.name,
            file_type: importFile.name.split('.').pop().toLowerCase()
          }, { params: { company_id: companyId } });
          
          if (response.data.products && response.data.products.length > 0) {
            setImportedItems(response.data.products);
            toast.success(`${response.data.products.length} produse extrase din ${response.data.source || 'document'}`);
          } else {
            toast.warning(response.data.error || "Nu s-au găsit produse în document");
          }
        } catch (err) {
          console.error("Extraction error:", err);
          toast.error(err.response?.data?.detail || "Eroare la extragerea produselor");
        }
        
        setImporting(false);
      };
      reader.readAsDataURL(importFile);
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Eroare la procesarea fișierului");
      setImporting(false);
    }
  };

  const handleImportSelected = async () => {
    const selectedItems = importedItems.filter(item => item.selected !== false);
    
    if (selectedItems.length === 0) {
      toast.error("Selectați cel puțin un produs");
      return;
    }
    
    try {
      for (const item of selectedItems) {
        await axios.post(`${API}/inventory`, {
          name: item.name,
          um: item.um || "buc",
          material_price: item.material_price || 0,
          manopera_price: item.manopera_price || 0,
          stock: item.stock || 0,
          supplier: item.supplier || "",
          category: item.category || "",
          code: item.code || ""
        }, { params: { company_id: companyId } });
      }
      
      toast.success(`${selectedItems.length} produse importate`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportedItems([]);
      fetchItems();
    } catch (error) {
      toast.error("Eroare la importarea produselor");
    }
  };

  const toggleImportItem = (index) => {
    const updated = [...importedItems];
    updated[index].selected = updated[index].selected === false ? true : false;
    setImportedItems(updated);
  };

  const updateImportItem = (index, field, value) => {
    const updated = [...importedItems];
    updated[index][field] = value;
    setImportedItems(updated);
  };

  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sorted items with useMemo for performance
  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    
    return [...filteredItems].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle different data types
      if (sortKey === 'material_price' || sortKey === 'manopera_price' || sortKey === 'stock') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortKey === 'created_at' || sortKey === 'updated_at') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      } else {
        // String comparison (name, supplier, etc.)
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredItems, sortKey, sortDirection]);

  // Handle sort click
  const handleSort = (key) => {
    if (sortKey === key) {
      // Toggle direction if same key
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New key, start with ascending
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortKey !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-teal-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const lowStockItems = items.filter(item => item.stock <= 5 && item.stock >= 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-teal-600" />
            Inventar
          </h2>
          <p className="text-slate-600 text-sm">{items.length} produse în gestiune</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowImportDialog(true)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Importă din Document
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />
            Adaugă Produs
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Produse ({items.length})
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Istoric
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Caută produs, furnizor, cod..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Bulk delete button */}
                {selectedItems.length > 0 && (
                  <Button 
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={isDeleting}
                    data-testid="bulk-delete-btn"
                  >
                    {isDeleting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Șterge {selectedItems.length} produse
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
              ) : sortedItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nu există produse în inventar</p>
                  <Button onClick={() => setShowAddDialog(true)} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă primul produs
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox 
                            checked={selectedItems.length === sortedItems.length && sortedItems.length > 0}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort('name')}
                          data-testid="sort-name"
                        >
                          <div className="flex items-center">
                            Produs
                            <SortIcon columnKey="name" />
                          </div>
                        </TableHead>
                        <TableHead className="text-center">U.M.</TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort('material_price')}
                          data-testid="sort-price"
                        >
                          <div className="flex items-center justify-end">
                            Preț Material
                            <SortIcon columnKey="material_price" />
                          </div>
                        </TableHead>
                        <TableHead className="text-right">Preț Manoperă</TableHead>
                        <TableHead className="text-center">Stoc</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort('supplier')}
                          data-testid="sort-supplier"
                        >
                          <div className="flex items-center">
                            Furnizor
                            <SortIcon columnKey="supplier" />
                          </div>
                        </TableHead>
                        <TableHead>Factură Proveniență</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort('created_at')}
                          data-testid="sort-date"
                        >
                          <div className="flex items-center">
                            Data
                            <SortIcon columnKey="created_at" />
                          </div>
                        </TableHead>
                        <TableHead className="text-right">Acțiuni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedItems.map((item) => (
                        <TableRow 
                          key={item.id} 
                          className={`${item.stock <= 5 ? 'bg-amber-50' : ''} ${selectedItems.includes(item.id) ? 'bg-blue-50' : ''}`} 
                          data-testid={`inventory-row-${item.id}`}
                        >
                          <TableCell>
                            <Checkbox 
                              checked={selectedItems.includes(item.id)}
                              onCheckedChange={() => toggleSelectItem(item.id)}
                              data-testid={`select-item-${item.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.name}</p>
                              {item.code && <p className="text-xs text-slate-500">Cod: {item.code}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{item.um}</TableCell>
                          <TableCell className="text-right">{formatNumber(item.material_price)} RON</TableCell>
                          <TableCell className="text-right">{formatNumber(item.manopera_price)} RON</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.stock <= 5 ? 'destructive' : 'secondary'}>
                              {item.stock} {item.um}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.supplier || '-'}</TableCell>
                          <TableCell>
                            {item.invoice_series || item.invoice_number ? (
                              <div className="text-xs">
                                <p className="font-medium text-blue-600">
                                  {item.invoice_series && item.invoice_number 
                                    ? `${item.invoice_series}/${item.invoice_number}`
                                    : item.invoice_series || item.invoice_number}
                                </p>
                                {item.invoice_date && (
                                  <p className="text-slate-500">{item.invoice_date}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('ro-RO') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => setEditingItem({...item})} data-testid={`edit-item-${item.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-item-${item.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Istoric Modificări Stoc
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {logs.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nu există înregistrări</p>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {log.quantity < 0 ? (
                            <TrendingDown className="w-5 h-5 text-red-500" />
                          ) : (
                            <TrendingUp className="w-5 h-5 text-green-500" />
                          )}
                          <div>
                            <p className="font-medium">{log.item_name}</p>
                            <p className="text-xs text-slate-500">{log.reason || 'Ajustare manuală'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${log.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {log.quantity > 0 ? '+' : ''}{log.quantity}
                          </p>
                          <p className="text-xs text-slate-500">{formatDate(log.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Product Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adaugă Produs Nou</DialogTitle>
            <DialogDescription>Completează detaliile produsului</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nume Produs *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                placeholder="Ex: Ciment Holcim 40kg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unitate Măsură</Label>
                <Select value={newItem.um} onValueChange={(v) => setNewItem({...newItem, um: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buc">buc</SelectItem>
                    <SelectItem value="mp">mp</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="mc">mc</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="l">l</SelectItem>
                    <SelectItem value="sac">sac</SelectItem>
                    <SelectItem value="set">set</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cod Produs</Label>
                <Input
                  value={newItem.code}
                  onChange={(e) => setNewItem({...newItem, code: e.target.value})}
                  placeholder="SKU / Cod intern"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preț Material (RON)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.material_price}
                  onChange={(e) => setNewItem({...newItem, material_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Preț Manoperă (RON)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.manopera_price}
                  onChange={(e) => setNewItem({...newItem, manopera_price: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stoc Inițial</Label>
                <Input
                  type="number"
                  value={newItem.stock}
                  onChange={(e) => setNewItem({...newItem, stock: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Furnizor</Label>
                <Input
                  value={newItem.supplier}
                  onChange={(e) => setNewItem({...newItem, supplier: e.target.value})}
                  placeholder="Nume furnizor"
                />
              </div>
            </div>
            
            {/* Trasabilitate - Factură de proveniență */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Trasabilitate (Factură proveniență)
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Serie Factură</Label>
                  <Input
                    value={newItem.invoice_series || ""}
                    onChange={(e) => setNewItem({...newItem, invoice_series: e.target.value.toUpperCase()})}
                    placeholder="Ex: ABC"
                  />
                </div>
                <div>
                  <Label>Număr Factură</Label>
                  <Input
                    value={newItem.invoice_number || ""}
                    onChange={(e) => setNewItem({...newItem, invoice_number: e.target.value})}
                    placeholder="Ex: 1234"
                  />
                </div>
                <div>
                  <Label>Data Factură</Label>
                  <Input
                    type="date"
                    value={newItem.invoice_date || ""}
                    onChange={(e) => setNewItem({...newItem, invoice_date: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Anulează</Button>
              <Button onClick={handleAddItem} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editează Produs</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              <div>
                <Label>Nume Produs *</Label>
                <Input
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unitate Măsură</Label>
                  <Select value={editingItem.um} onValueChange={(v) => setEditingItem({...editingItem, um: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buc">buc</SelectItem>
                      <SelectItem value="mp">mp</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="mc">mc</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="l">l</SelectItem>
                      <SelectItem value="sac">sac</SelectItem>
                      <SelectItem value="set">set</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stoc Curent</Label>
                  <Input
                    type="number"
                    value={editingItem.stock}
                    onChange={(e) => setEditingItem({...editingItem, stock: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preț Material (RON)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.material_price}
                    onChange={(e) => setEditingItem({...editingItem, material_price: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Preț Manoperă (RON)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.manopera_price}
                    onChange={(e) => setEditingItem({...editingItem, manopera_price: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div>
                <Label>Furnizor</Label>
                <Input
                  value={editingItem.supplier || ''}
                  onChange={(e) => setEditingItem({...editingItem, supplier: e.target.value})}
                />
              </div>
              
              {/* Trasabilitate - Factură de proveniență */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Trasabilitate (Factură proveniență)
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Serie Factură</Label>
                    <Input
                      value={editingItem.invoice_series || ""}
                      onChange={(e) => setEditingItem({...editingItem, invoice_series: e.target.value.toUpperCase()})}
                      placeholder="Ex: ABC"
                    />
                  </div>
                  <div>
                    <Label>Număr Factură</Label>
                    <Input
                      value={editingItem.invoice_number || ""}
                      onChange={(e) => setEditingItem({...editingItem, invoice_number: e.target.value})}
                      placeholder="Ex: 1234"
                    />
                  </div>
                  <div>
                    <Label>Data Factură</Label>
                    <Input
                      type="date"
                      value={editingItem.invoice_date || ""}
                      onChange={(e) => setEditingItem({...editingItem, invoice_date: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingItem(null)}>Anulează</Button>
                <Button onClick={handleUpdateItem} className="bg-teal-600 hover:bg-teal-700">
                  <Check className="w-4 h-4 mr-2" />
                  Salvează
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              Importă Produse din Document
            </DialogTitle>
            <DialogDescription>
              Încarcă o factură (PDF, Excel sau XML) și AI-ul va extrage automat produsele din secțiunea ARTICOL
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                accept=".pdf,.xlsx,.xls,.xml"
                onChange={handleFileUpload}
                className="hidden"
                id="doc-upload"
              />
              <label htmlFor="doc-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600 font-medium">
                  {importFile ? importFile.name : 'Click pentru a selecta un fișier'}
                </p>
                <p className="text-xs text-slate-400 mt-1">Formate acceptate: PDF, Excel (.xlsx, .xls), XML</p>
              </label>
            </div>
            
            {importFile && !importedItems.length && (
              <Button 
                onClick={handleAIImport} 
                disabled={importing}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se procesează...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Extrage Produse cu AI
                  </>
                )}
              </Button>
            )}
            
            {/* Extracted Products */}
            {importedItems.length > 0 && (
              <div className="space-y-3">
                <p className="font-semibold text-slate-700">
                  Produse extrase ({importedItems.filter(i => i.selected !== false).length} selectate):
                </p>
                <ScrollArea className="h-[350px] border rounded-lg p-2">
                  {importedItems.map((item, idx) => (
                    <div 
                      key={item.name ? `imp-${item.name}-${idx}` : `imp-${idx}`}
                      className={`p-3 rounded-lg mb-2 ${
                        item.selected === false ? 'bg-slate-100 opacity-50' : 'bg-green-50 border border-green-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <input
                          type="checkbox"
                          checked={item.selected !== false}
                          onChange={() => toggleImportItem(idx)}
                          className="w-5 h-5 flex-shrink-0"
                        />
                        <Input
                          value={item.name}
                          onChange={(e) => updateImportItem(idx, 'name', e.target.value)}
                          placeholder="Denumire produs"
                          className="flex-1"
                        />
                      </div>
                      <div className="grid grid-cols-5 gap-2 ml-8">
                        <div>
                          <Label className="text-xs text-slate-500">U.M.</Label>
                          <Input
                            value={item.um || 'buc'}
                            onChange={(e) => updateImportItem(idx, 'um', e.target.value)}
                            placeholder="UM"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Cantitate</Label>
                          <Input
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => updateImportItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Preț Unitar</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.material_price || 0}
                            onChange={(e) => updateImportItem(idx, 'material_price', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Stoc Inițial</Label>
                          <Input
                            type="number"
                            value={item.stock || item.quantity || 0}
                            onChange={(e) => updateImportItem(idx, 'stock', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Furnizor</Label>
                          <Input
                            value={item.supplier || ''}
                            onChange={(e) => updateImportItem(idx, 'supplier', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setImportedItems([]);
                    setImportFile(null);
                  }}>
                    Resetează
                  </Button>
                  <Button onClick={handleImportSelected} className="bg-green-600 hover:bg-green-700">
                    <Check className="w-4 h-4 mr-2" />
                    Importă Selectate
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
